/**
 * EndOfDaySummary page component
 * Displays comprehensive end-of-day production summary for supervisors
 * Features:
 * - Date picker (defaults to today)
 * - Summary cards (sessions, boxes, weight, yield)
 * - Alerts section (low yield, high void rate, incomplete sessions)
 * - Tabbed breakdown: By Product, By Station, By Worker, All Sessions
 * - Export options: Print, CSV, PDF
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import * as XLSX from 'xlsx-js-style';
import { useLanguage } from '../../i18n/LanguageContext';
import { wsService } from '../../services/websocket';
import productionService from '../../services/productionService';
import { QUERY_REFETCH_SLOW } from '../../constants/queryConfig';
import { dailyTransferOutService } from '../../services/dailyTransferOutService';
import { formatActiveHours } from '../../utils/dateUtils';
import {
  formatYield,
  getItemActualYF,
  getItemYieldVariance,
  getYieldVarianceHexColor,
  formatYieldVariancePct,
} from '../../hooks/useYieldFactor';
import SummaryCards from './components/EndOfDaySummary/SummaryCards';
import AlertsSection from './components/EndOfDaySummary/AlertsSection';
import ProductTable from './components/EndOfDaySummary/ProductTable';
import StationTable from './components/EndOfDaySummary/StationTable';
import WorkerTable from './components/EndOfDaySummary/WorkerTable';
import SessionsTable from './components/EndOfDaySummary/SessionsTable';
import WasteTable from './components/EndOfDaySummary/WasteTable';
import TransferOutSection from './components/EndOfDaySummary/TransferOutSection';
import {
  Printer,
  Download,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
} from 'lucide-react';
import DatePickerDropdown from '../../components/DatePickerDropdown';

type TabType = 'product' | 'station' | 'worker' | 'sessions' | 'waste';

// Helper to get local date string (YYYY-MM-DD) without UTC conversion
const getLocalDateString = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface EndOfDaySummaryProps {
  embedded?: boolean; // When true, hides page title (used in Reports tab view)
  summaryCollapsed?: boolean; // When true, hides summary cards
}

const EndOfDaySummary: React.FC<EndOfDaySummaryProps> = ({ embedded = false, summaryCollapsed = false }) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(() => getLocalDateString());
  const [activeTab, setActiveTab] = useState<TabType>('product');
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  // dateInputRef removed — DatePickerDropdown handles its own state

  // Close mobile actions menu when clicking outside
  useEffect(() => {
    if (!actionsMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target as Node)) {
        setActionsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [actionsMenuOpen]);

  // Fetch end-of-day summary data
  const { data, isLoading, isError, refetch } = useQuery(
    ['end-of-day-summary', selectedDate],
    () => productionService.getEndOfDaySummary(selectedDate),
    {
      staleTime: 60000,
      keepPreviousData: true,
      refetchInterval: QUERY_REFETCH_SLOW,
    }
  );

  // Fetch transfer-out data for the selected date
  const { data: transferOutData, isLoading: transferOutLoading } = useQuery(
    ['eod-transfer-out', selectedDate],
    () => dailyTransferOutService.getDailyTransferOut(selectedDate),
    {
      staleTime: 60000,
      keepPreviousData: true,
      retry: false, // 404 = no data for this date, don't retry
      onError: () => {}, // Silently handle — no transfer data is normal
    }
  );

  // WebSocket: refresh transfer-out data when a transfer is completed by the poller
  const handleTransferWs = useCallback(() => {
    queryClient.invalidateQueries(['eod-transfer-out', selectedDate]);
  }, [queryClient, selectedDate]);

  // WebSocket: refresh summary when any production activity changes output numbers
  const handleProductionWs = useCallback(() => {
    queryClient.invalidateQueries(['end-of-day-summary', selectedDate]);
  }, [queryClient, selectedDate]);

  useEffect(() => {
    wsService.connect();
    wsService.on('hte-transfer-updated', handleTransferWs);
    wsService.on('box-packed', handleProductionWs);
    wsService.on('box-cancelled', handleProductionWs);
    wsService.on('box-deleted', handleProductionWs);
    wsService.on('waste-recorded', handleProductionWs);
    wsService.on('production-session-updated', handleProductionWs);
    wsService.on('production-session-created', handleProductionWs);
    wsService.on('pending-label-voided', handleProductionWs);
    return () => {
      wsService.off('hte-transfer-updated', handleTransferWs);
      wsService.off('box-packed', handleProductionWs);
      wsService.off('box-cancelled', handleProductionWs);
      wsService.off('box-deleted', handleProductionWs);
      wsService.off('waste-recorded', handleProductionWs);
      wsService.off('production-session-updated', handleProductionWs);
      wsService.off('production-session-created', handleProductionWs);
      wsService.off('pending-label-voided', handleProductionWs);
    };
  }, [handleTransferWs, handleProductionWs]);

  // Date navigation helpers — parse date parts directly to avoid timezone issues
  const navigateDate = (direction: 'prev' | 'next') => {
    const [year, month, day] = selectedDate.split('-').map(Number);
    const current = new Date(year, month - 1, day); // local midnight
    if (direction === 'prev') {
      current.setDate(current.getDate() - 1);
    } else {
      current.setDate(current.getDate() + 1);
    }
    setSelectedDate(getLocalDateString(current));
  };

  const isToday = selectedDate === getLocalDateString();

  // Handler to dismiss freshness alerts
  const handleDismissAlert = async (alertKey: string) => {
    try {
      await productionService.dismissAlert(alertKey);
      // Refetch to update alerts list
      await refetch();
    } catch (error) {
      console.error('Failed to dismiss alert:', error);
      throw error;
    }
  };

  // Format date as "Wed - 05/13/26"
  const formatDateForDisplay = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
    const mm = String(month).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    const yy = String(year).slice(-2);
    return `${dayName} - ${mm}/${dd}/${yy}`;
  };

  // Export functions
  const handlePrint = () => {
    if (!data) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Build By Product table — includes raw materials if available
    const hasRawMaterials = (data.by_product || []).some(p => p.raw_materials && p.raw_materials.length > 0);
    const productRows = (data.by_product || []).map(p => {
      const materials = p.raw_materials || [];
      const materialCell = materials.length > 0
        ? materials.map(m =>
            `<div style="margin-bottom: 1px; font-size: 10px;">${m.item_number} - ${m.item_name || '?'}${m.item_chinese_name ? ` (${m.item_chinese_name})` : ''}</div>`
          ).join('')
        : '<span style="color: #999;">—</span>';
      const allocatedCell = materials.length > 0
        ? materials.map(m =>
            `<div style="margin-bottom: 1px; font-size: 10px;">${m.allocated_weight.toFixed(2)} lb</div>`
          ).join('') + (materials.length > 1 ? `<div style="border-top: 1px solid #ddd; font-size: 10px; font-weight: 600;">${(p.initial_material_weight || 0).toFixed(2)} lb</div>` : '')
        : '<span style="color: #999;">—</span>';
      const usedCell = materials.length > 0
        ? materials.map(m =>
            `<div style="margin-bottom: 1px; font-size: 10px;">${m.used_weight != null ? m.used_weight.toFixed(2) + ' lb' : '—'}</div>`
          ).join('') + (materials.length > 1 ? `<div style="border-top: 1px solid #ddd; font-size: 10px; font-weight: 600;">${materials.some(m => m.used_weight != null) ? materials.filter(m => m.used_weight != null).reduce((s, m) => s + (m.used_weight || 0), 0).toFixed(2) + ' lb' : '—'}</div>` : '')
        : '<span style="color: #999;">—</span>';
      return `
        <tr>
          <td>${p.product_sku || '-'}</td>
          <td>${p.product_name || '-'}${p.product_chinese_name ? ` (${p.product_chinese_name})` : ''}</td>
          ${hasRawMaterials ? `<td>${materialCell}</td><td style="text-align: right;">${allocatedCell}</td><td style="text-align: right;">${usedCell}</td>` : ''}
          <td style="text-align: center;">${p.sessions_count || 0}</td>
          <td style="text-align: center;">${p.boxes_packed || 0}</td>
          <td style="text-align: right;">${(p.total_weight || 0).toFixed(2)}</td>
          <td style="text-align: right;">${formatYield(p.yield_percentage || 0)}</td>
        </tr>
      `;
    }).join('');

    // Build By Station table
    const stationRows = (data.by_station || []).map(s => `
      <tr>
        <td>${s.station_name || '-'}</td>
        <td>${s.location_name || '-'}</td>
        <td style="text-align: center;">${s.sessions_count || 0}</td>
        <td style="text-align: center;">${s.boxes_packed || 0}</td>
        <td style="text-align: right;">${(s.total_weight || 0).toFixed(2)}</td>
        <td style="text-align: right;">${formatActiveHours(s.active_hours || 0)}</td>
      </tr>
    `).join('');

    // Build By Worker table
    const workerRows = (data.by_worker || []).map(w => `
      <tr>
        <td>${w.employee_number || '-'}</td>
        <td>${w.worker_name || '-'}</td>
        <td style="text-align: center;">${w.sessions_worked || 0}</td>
        <td style="text-align: center;">${w.boxes_packed || 0}</td>
        <td style="text-align: right;">${(w.total_weight || 0).toFixed(2)}</td>
        <td style="text-align: right;">${(w.avg_pace || 0).toFixed(1)}</td>
      </tr>
    `).join('');

    // Build All Sessions table
    const sessionRows = (data.sessions || []).map(s => `
      <tr>
        <td>${s.session_number || '-'}</td>
        <td>
          ${s.product_name || '-'}${(s as any).product_chinese_name ? ` (${(s as any).product_chinese_name})` : ''}
          <div style="font-size: 10px; color: #666;">${(s as any).product_sku || '-'}</div>
        </td>
        <td>${s.status || '-'}</td>
        <td style="text-align: center;">${s.boxes_packed || 0}</td>
        <td style="text-align: right;">${(s.total_weight || 0).toFixed(2)}</td>
        <td style="text-align: right;">${formatYield(s.yield_percentage || 0)}</td>
      </tr>
    `).join('');

    // Build Waste Log by Session with breakdown
    const wasteBySession = data.waste_by_session || [];
    const wasteTypeLabels: Record<string, string> = {
      by_product: 'By-Product',
      by_product_marketable: 'Byproduct - Marketable',
      by_product_production: 'Byproduct - Production',
      disposable: 'Disposable',
      water_liquid: 'Water/Liquid',
      human_error: 'Human Error',
      remainder_intact: 'Remainder (Intact)',
      remainder_not_intact: 'Remainder (Not Intact)',
    };
    const totalWaste = wasteBySession.reduce((sum, s) => sum + s.total_waste, 0);
    const actualWasteTypes = ['disposable', 'human_error', 'water_liquid'];
    const returnsTypes = ['by_product', 'by_product_marketable', 'by_product_production', 'remainder_intact', 'remainder_not_intact'];

    const renderWasteColumn = (items: typeof wasteBySession[0]['waste_types'], totalWaste: number) => {
      if (items.length === 0) return '<span style="font-size: 9px; color: #999;">—</span>';
      return items.map(wt => `
        <div style="margin-bottom: 2px;">
          <span style="font-size: 9px; color: #666;">${wasteTypeLabels[wt.waste_type] || wt.waste_type}:</span>
          <span style="font-weight: 600;">${wt.weight.toFixed(2)} lb</span>
          <span style="font-size: 9px; color: #999;">(${totalWaste > 0 ? ((wt.weight / totalWaste) * 100).toFixed(2) : '0.00'}%)</span>
        </div>
      `).join('');
    };

    const wasteSessionRows = wasteBySession.map(s => {
      const wasteItems = s.waste_types.filter(wt => actualWasteTypes.includes(wt.waste_type));
      const returnItems = s.waste_types.filter(wt => returnsTypes.includes(wt.waste_type));
      const wasteSubtotal = wasteItems.reduce((sum, wt) => sum + wt.weight, 0);
      const returnSubtotal = returnItems.reduce((sum, wt) => sum + wt.weight, 0);
      return `
        <tr>
          <td>${s.session_number || '-'}</td>
          <td>${s.product_name || '-'}${s.product_chinese_name ? `<br/><span style="font-size: 10px; color: #444;">${s.product_chinese_name}</span>` : ''}<br/><span style="font-size: 9px; color: #666;">${s.product_sku}</span></td>
          <td>
            ${renderWasteColumn(wasteItems, s.total_waste)}
            <div style="margin-top: 4px; padding-top: 3px; border-top: 1px solid #eee; font-size: 9px; font-weight: 600; color: #dc2626;">Subtotal: ${wasteSubtotal.toFixed(2)} lb</div>
          </td>
          <td>
            ${renderWasteColumn(returnItems, s.total_waste)}
            <div style="margin-top: 4px; padding-top: 3px; border-top: 1px solid #eee; font-size: 9px; font-weight: 600; color: #16a34a;">Subtotal: ${returnSubtotal.toFixed(2)} lb</div>
          </td>
          <td style="text-align: right; font-weight: 600;">${s.total_waste.toFixed(2)}</td>
        </tr>
      `;
    }).join('');

    // Summary section
    const summary = data.summary || {};
    const summaryHtml = `
      <div class="summary">
        <div class="summary-item"><strong>Total Sessions:</strong> ${summary.total_sessions || 0} (${summary.completed_sessions || 0} completed)</div>
        <div class="summary-item"><strong>Boxes Packed:</strong> ${summary.total_boxes || 0}</div>
        <div class="summary-item"><strong>Total Weight:</strong> ${(summary.total_weight_lbs || 0).toFixed(2)} lb</div>
        <div class="summary-item"><strong>Total Waste:</strong> ${(summary.total_waste_lbs || 0).toFixed(2)} lb</div>
        <div class="summary-item"><strong>Avg Yield:</strong> ${formatYield(summary.avg_yield_percentage || 0)}</div>
      </div>
    `;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>End of Day Summary - ${selectedDate}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { font-size: 18px; margin-bottom: 5px; }
          h2 { font-size: 14px; margin-top: 25px; margin-bottom: 10px; padding-bottom: 5px; border-bottom: 2px solid #3b82f6; color: #1e40af; }
          .subtitle { font-size: 14px; color: #666; margin-bottom: 15px; }
          .summary { margin-bottom: 20px; font-size: 14px; display: flex; gap: 30px; flex-wrap: wrap; }
          .summary-item { padding: 8px 16px; background: #f3f4f6; border-radius: 4px; }
          table { border-collapse: collapse; width: 100%; font-size: 11px; margin-bottom: 20px; }
          th { background-color: #e5e7eb; color: #111; padding: 8px 6px; text-align: left; border: 1px solid #d1d5db; font-weight: 600; }
          td { border: 1px solid #ddd; padding: 6px; }
          tr:nth-child(even) { background-color: #f9fafb; }
          .section { page-break-inside: avoid; }
          @media print {
            body { margin: 0; padding: 10px; }
            h1 { font-size: 16px; }
            h2 { font-size: 12px; margin-top: 15px; }
          }
        </style>
      </head>
      <body>
        <h1>End of Day Summary</h1>
        <div class="subtitle">${selectedDate}</div>
        ${summaryHtml}

        <div class="section">
          <h2>Products</h2>
          <table>
            <thead>
              <tr>
                <th>Item Number</th>
                <th>Product Name</th>
                ${hasRawMaterials ? '<th>Raw Material</th><th style="text-align: right;">Allocated</th><th style="text-align: right;">Used</th>' : ''}
                <th style="text-align: center;">Sessions</th>
                <th style="text-align: center;">Boxes</th>
                <th style="text-align: right;">Weight (lb)</th>
                <th style="text-align: right;">Yield (%)</th>
              </tr>
            </thead>
            <tbody>${productRows || `<tr><td colspan="${hasRawMaterials ? 9 : 6}" style="text-align: center; color: #666;">No data</td></tr>`}</tbody>
          </table>
        </div>

        <div class="section">
          <h2>Stations</h2>
          <table>
            <thead>
              <tr>
                <th>Station</th>
                <th>Location</th>
                <th style="text-align: center;">Sessions</th>
                <th style="text-align: center;">Boxes</th>
                <th style="text-align: right;">Weight (lb)</th>
                <th style="text-align: right;">Active Hours</th>
              </tr>
            </thead>
            <tbody>${stationRows || '<tr><td colspan="6" style="text-align: center; color: #666;">No data</td></tr>'}</tbody>
          </table>
        </div>

        <div class="section">
          <h2>Operators</h2>
          <table>
            <thead>
              <tr>
                <th>Employee #</th>
                <th>Operator Name</th>
                <th style="text-align: center;">Sessions</th>
                <th style="text-align: center;">Boxes</th>
                <th style="text-align: right;">Weight (lb)</th>
                <th style="text-align: right;">Pace (lb/hr)</th>
              </tr>
            </thead>
            <tbody>${workerRows || '<tr><td colspan="6" style="text-align: center; color: #666;">No data</td></tr>'}</tbody>
          </table>
        </div>

        <div class="section">
          <h2>All Sessions</h2>
          <table>
            <thead>
              <tr>
                <th>Session #</th>
                <th>Product</th>
                <th>Status</th>
                <th style="text-align: center;">Boxes</th>
                <th style="text-align: right;">Weight (lb)</th>
                <th style="text-align: right;">Yield (%)</th>
              </tr>
            </thead>
            <tbody>${sessionRows || '<tr><td colspan="6" style="text-align: center; color: #666;">No data</td></tr>'}</tbody>
          </table>
        </div>

        <div class="section">
          <h2>Waste Log</h2>
          <div style="margin-bottom: 10px; font-size: 12px;">
            <strong>Total Waste:</strong> ${totalWaste.toFixed(2)} lb
          </div>
          <table>
            <thead>
              <tr>
                <th>Session #</th>
                <th>Product</th>
                <th style="color: #dc2626;">Actual Waste</th>
                <th style="color: #16a34a;">Returns to Inventory</th>
                <th style="text-align: right;">Total (lb)</th>
              </tr>
            </thead>
            <tbody>${wasteSessionRows || '<tr><td colspan="5" style="text-align: center; color: #666;">No waste logged</td></tr>'}</tbody>
          </table>
        </div>

        ${transferOutData ? `
        <div class="section">
          <h2>Transfer Out Summary</h2>
          <table>
            <thead>
              <tr>
                <th>Item No.</th>
                <th>Item Name</th>
                <th>Packing</th>
                <th style="text-align: right;">Exp. Qty</th>
                <th style="text-align: right;">Exp. Weight</th>
                <th style="text-align: right;">Act. Qty</th>
                <th style="text-align: right;">Act. Weight</th>
                <th style="text-align: right;">Exp. YF</th>
                <th style="text-align: right;">Act. YF</th>
                <th style="text-align: right;">YF Variance</th>
              </tr>
            </thead>
            <tbody>
              ${(transferOutData.finished_products || []).length > 0
                ? (transferOutData.finished_products || []).map(item => {
                    const actualYF = getItemActualYF(item);
                    const expectedYF = (item.yield_factor != null && Number(item.yield_factor) > 0) ? Number(item.yield_factor) : null;
                    const yieldVariance = getItemYieldVariance(item);
                    return `<tr>
                      <td>${item.item_number}</td>
                      <td>${item.item_name || '-'}${item.item_chinese_name ? ` (${item.item_chinese_name})` : ''}</td>
                      <td>${item.packing || '-'}</td>
                      <td style="text-align: right;">${item.expected_quantity != null ? Number(item.expected_quantity).toLocaleString() : '-'}</td>
                      <td style="text-align: right;">${item.expected_weight != null ? Number(item.expected_weight).toFixed(2) : '-'}</td>
                      <td style="text-align: right;">${item.actual_quantity != null ? Number(item.actual_quantity).toLocaleString() : '-'}</td>
                      <td style="text-align: right;">${item.actual_weight != null ? Number(item.actual_weight).toFixed(2) : '-'}</td>
                      <td style="text-align: right;">${expectedYF != null ? formatYield(expectedYF) : '?'}</td>
                      <td style="text-align: right;">${actualYF != null ? formatYield(actualYF) : '-'}</td>
                      <td style="text-align: right; color: ${getYieldVarianceHexColor(yieldVariance)};">${formatYieldVariancePct(yieldVariance)}</td>
                    </tr>`;
                  }).join('')
                : '<tr><td colspan="10" style="text-align: center; color: #666;">No finished products</td></tr>'
              }
            </tbody>
          </table>

          ${(transferOutData.rejected_materials || []).length > 0 ? `
          <h3 style="font-size: 12px; margin-top: 15px; margin-bottom: 5px; color: #dc2626;">Rejected Materials (${transferOutData.rejected_materials.length})</h3>
          <table>
            <thead><tr><th>Item No.</th><th>Item Name</th><th style="text-align: right;">Act. Qty</th><th style="text-align: right;">Act. Weight</th><th>Notes</th></tr></thead>
            <tbody>${transferOutData.rejected_materials.map(item => `<tr><td>${item.item_number}</td><td>${item.item_name || '-'}${item.item_chinese_name ? ` (${item.item_chinese_name})` : ''}</td><td style="text-align: right;">${item.actual_quantity != null ? Number(item.actual_quantity).toLocaleString() : '-'}</td><td style="text-align: right;">${item.actual_weight != null ? Number(item.actual_weight).toFixed(2) : '-'}</td><td>${item.notes || '-'}</td></tr>`).join('')}</tbody>
          </table>
          ` : ''}

          ${(transferOutData.remaining_materials || []).length > 0 ? `
          <h3 style="font-size: 12px; margin-top: 15px; margin-bottom: 5px; color: #2563eb;">Available Remaining Raw Material (${transferOutData.remaining_materials.length})</h3>
          <table>
            <thead><tr><th>Item No.</th><th>Item Name</th><th style="text-align: right;">Act. Qty</th><th style="text-align: right;">Act. Weight</th><th>Notes</th></tr></thead>
            <tbody>${transferOutData.remaining_materials.map(item => `<tr><td>${item.item_number}</td><td>${item.item_name || '-'}${item.item_chinese_name ? ` (${item.item_chinese_name})` : ''}</td><td style="text-align: right;">${item.actual_quantity != null ? Number(item.actual_quantity).toLocaleString() : '-'}</td><td style="text-align: right;">${item.actual_weight != null ? Number(item.actual_weight).toFixed(2) : '-'}</td><td>${item.notes || '-'}</td></tr>`).join('')}</tbody>
          </table>
          ` : ''}

          ${(transferOutData.by_products || []).length > 0 ? `
          <h3 style="font-size: 12px; margin-top: 15px; margin-bottom: 5px; color: #7c3aed;">By-Products (${transferOutData.by_products.length})</h3>
          <table>
            <thead><tr><th>Item No.</th><th>Item Name</th><th style="text-align: right;">Act. Qty</th><th style="text-align: right;">Act. Weight</th><th>Notes</th></tr></thead>
            <tbody>${transferOutData.by_products.map(item => `<tr><td>${item.item_number}</td><td>${item.item_name || '-'}${item.item_chinese_name ? ` (${item.item_chinese_name})` : ''}</td><td style="text-align: right;">${item.actual_quantity != null ? Number(item.actual_quantity).toLocaleString() : '-'}</td><td style="text-align: right;">${item.actual_weight != null ? Number(item.actual_weight).toFixed(2) : '-'}</td><td>${item.notes || '-'}</td></tr>`).join('')}</tbody>
          </table>
          ` : ''}
        </div>
        ` : ''}
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleExportExcel = () => {
    if (!data) return;

    const excelActualWasteTypes = ['disposable', 'human_error', 'water_liquid'];
    const excelReturnsTypes = ['by_product', 'by_product_marketable', 'by_product_production', 'remainder_intact', 'remainder_not_intact'];
    const excelWasteTypeLabels: Record<string, string> = {
      by_product: 'By-Product',
      by_product_marketable: 'Byproduct - Marketable',
      by_product_production: 'Byproduct - Production',
      disposable: 'Disposable',
      water_liquid: 'Water/Liquid',
      human_error: 'Human Error',
      remainder_intact: 'Remainder (Intact)',
      remainder_not_intact: 'Remainder (Not Intact)',
    };

    // Build all sections into a single sheet, separated by empty rows
    const allRows: (string | number)[][] = [];

    // 1. Products section — includes raw material breakdown per product
    // Multi-material products use newlines within cells so each product stays on one row
    const excelHasRawMaterials = data.by_product.some(p => p.raw_materials && p.raw_materials.length > 0);
    const productStartRow = allRows.length;
    allRows.push(['PRODUCTS']);
    if (excelHasRawMaterials) {
      allRows.push(['Product', 'Raw Material', 'Allocated (lb)', 'Used (lb)', 'Sessions', 'Boxes', 'Weight (lb)', 'Yield (%)']);
      data.by_product.forEach(p => {
        const materials = p.raw_materials || [];
        if (materials.length === 0) {
          allRows.push([
            `${p.product_sku} - ${p.product_name}${p.product_chinese_name ? ` (${p.product_chinese_name})` : ''}`,
            '', '', '',
            p.sessions_count, p.boxes_packed,
            Number(p.total_weight.toFixed(2)), Number((p.yield_percentage * 100).toFixed(2))
          ]);
        } else {
          const matNames = materials.map(m => `${m.item_number} - ${m.item_name}${m.item_chinese_name ? ` (${m.item_chinese_name})` : ''}`).join('\n');
          const matAllocated = materials.map(m => `${m.allocated_weight.toFixed(2)}`).join('\n')
            + (materials.length > 1 ? `\nTotal: ${(p.initial_material_weight || 0).toFixed(2)}` : '');
          const matUsed = materials.map(m => m.used_weight != null ? `${m.used_weight.toFixed(2)}` : '—').join('\n')
            + (materials.length > 1 ? `\nTotal: ${materials.some(m => m.used_weight != null) ? materials.filter(m => m.used_weight != null).reduce((s, m) => s + (m.used_weight || 0), 0).toFixed(2) : '—'}` : '');
          allRows.push([
            `${p.product_sku} - ${p.product_name}${p.product_chinese_name ? ` (${p.product_chinese_name})` : ''}`,
            matNames, matAllocated, matUsed,
            p.sessions_count, p.boxes_packed,
            Number(p.total_weight.toFixed(2)), Number((p.yield_percentage * 100).toFixed(2))
          ]);
        }
      });
    } else {
      allRows.push(['Product', 'Sessions', 'Boxes', 'Weight (lb)', 'Yield (%)']);
      data.by_product.forEach(p => allRows.push([
        `${p.product_sku} - ${p.product_name}${p.product_chinese_name ? ` (${p.product_chinese_name})` : ''}`,
        p.sessions_count, p.boxes_packed,
        Number(p.total_weight.toFixed(2)), Number((p.yield_percentage * 100).toFixed(2))
      ]));
    }
    const productEndRow = allRows.length - 1;
    allRows.push([]);

    // 2. Stations section
    allRows.push(['STATIONS']);
    allRows.push(['Station', 'Location', 'Sessions', 'Boxes', 'Weight (lb)', 'Active Hours']);
    data.by_station.forEach(s => allRows.push([
      s.station_name, s.location_name || '',
      s.sessions_count, s.boxes_packed,
      Number(s.total_weight.toFixed(2)), formatActiveHours(s.active_hours)
    ]));
    allRows.push([]);

    // 3. Operators section
    allRows.push(['OPERATORS']);
    allRows.push(['Employee #', 'Worker Name', 'Sessions', 'Boxes', 'Weight (lb)', 'Pace (lb/hr)']);
    data.by_worker.forEach(w => allRows.push([
      w.employee_number, w.worker_name,
      w.sessions_worked, w.boxes_packed,
      Number(w.total_weight.toFixed(2)), Number(w.avg_pace.toFixed(1))
    ]));
    allRows.push([]);

    // 4. All Sessions section
    allRows.push(['ALL SESSIONS']);
    allRows.push(['Session #', 'Product', 'Status', 'Boxes', 'Weight (lb)', 'Yield (%)']);
    data.sessions.forEach(s => allRows.push([
      s.session_number,
      `${(s as any).product_sku || ''} - ${s.product_name}${(s as any).product_chinese_name ? ` (${(s as any).product_chinese_name})` : ''}`,
      s.status, s.boxes_packed,
      Number(s.total_weight.toFixed(2)), Number((s.yield_percentage * 100).toFixed(2))
    ]));
    allRows.push([]);

    // 5. Waste Log section — one column per waste type with colored backgrounds
    const wasteLogStartRow = allRows.length;
    allRows.push(['WASTE LOG']);
    allRows.push([
      'Session #', 'Product',
      'Disposable', 'Human Err', 'Water/Liq', 'Waste Sub',
      'Marketable', 'Production', 'Rem Intact', 'Rem Not Int', 'Ret Sub',
      'Total (lb)'
    ]);
    (data.waste_by_session || []).forEach(s => {
      const getWeight = (type: string) => {
        const found = s.waste_types.find(wt => wt.waste_type === type);
        return found ? Number(found.weight.toFixed(2)) : 0;
      };
      const disposable = getWeight('disposable');
      const humanError = getWeight('human_error');
      const waterLiquid = getWeight('water_liquid');
      const marketable = getWeight('by_product_marketable');
      const production = getWeight('by_product_production');
      const remainderIntact = getWeight('remainder_intact');
      const remainderNotIntact = getWeight('remainder_not_intact');
      allRows.push([
        s.session_number,
        `${s.product_sku} - ${s.product_name}${s.product_chinese_name ? ` (${s.product_chinese_name})` : ''}`,
        disposable, humanError, waterLiquid, Number((disposable + humanError + waterLiquid).toFixed(2)),
        marketable, production, remainderIntact, remainderNotIntact, Number((marketable + production + remainderIntact + remainderNotIntact).toFixed(2)),
        Number(s.total_waste.toFixed(2))
      ]);
    });

    // 6. Transfer Out sections
    if (transferOutData) {
      allRows.push([]);
      allRows.push(['TRANSFER OUT — FINISHED PRODUCTS']);
      allRows.push(['Item No.', 'Item Name', 'Packing', 'Exp. Qty', 'Exp. Weight', 'Act. Qty', 'Act. Weight', 'Exp. YF', 'Act. YF', 'YF Variance %']);
      (transferOutData.finished_products || []).forEach(item => {
        const actualYF = getItemActualYF(item);
        const expectedYF = (item.yield_factor != null && Number(item.yield_factor) > 0) ? Number(item.yield_factor) : null;
        const yieldVariance = getItemYieldVariance(item);
        allRows.push([
          item.item_number,
          `${item.item_name || '-'}${item.item_chinese_name ? ` (${item.item_chinese_name})` : ''}`,
          item.packing || '-',
          item.expected_quantity != null ? Number(item.expected_quantity) : '',
          item.expected_weight != null ? Number(Number(item.expected_weight).toFixed(2)) : '',
          item.actual_quantity != null ? Number(item.actual_quantity) : '',
          item.actual_weight != null ? Number(Number(item.actual_weight).toFixed(2)) : '',
          expectedYF != null ? Number((expectedYF * 100).toFixed(2)) : '',
          actualYF != null ? Number((actualYF * 100).toFixed(2)) : '',
          yieldVariance != null ? Number(yieldVariance.toFixed(2)) : '',
        ]);
      });

      allRows.push([]);
      allRows.push(['TRANSFER OUT — REJECTED MATERIALS']);
      allRows.push(['Item No.', 'Item Name', 'Act. Qty', 'Act. Weight', 'Notes']);
      (transferOutData.rejected_materials || []).forEach(item => {
        allRows.push([
          item.item_number,
          `${item.item_name || '-'}${item.item_chinese_name ? ` (${item.item_chinese_name})` : ''}`,
          item.actual_quantity != null ? Number(item.actual_quantity) : '',
          item.actual_weight != null ? Number(Number(item.actual_weight).toFixed(2)) : '',
          item.notes || '',
        ]);
      });

      allRows.push([]);
      allRows.push(['TRANSFER OUT — REMAINING RAW MATERIALS']);
      allRows.push(['Item No.', 'Item Name', 'Act. Qty', 'Act. Weight', 'Notes']);
      (transferOutData.remaining_materials || []).forEach(item => {
        allRows.push([
          item.item_number,
          `${item.item_name || '-'}${item.item_chinese_name ? ` (${item.item_chinese_name})` : ''}`,
          item.actual_quantity != null ? Number(item.actual_quantity) : '',
          item.actual_weight != null ? Number(Number(item.actual_weight).toFixed(2)) : '',
          item.notes || '',
        ]);
      });

      allRows.push([]);
      allRows.push(['TRANSFER OUT — BY-PRODUCTS']);
      allRows.push(['Item No.', 'Item Name', 'Act. Qty', 'Act. Weight', 'Notes']);
      (transferOutData.by_products || []).forEach(item => {
        allRows.push([
          item.item_number,
          `${item.item_name || '-'}${item.item_chinese_name ? ` (${item.item_chinese_name})` : ''}`,
          item.actual_quantity != null ? Number(item.actual_quantity) : '',
          item.actual_weight != null ? Number(Number(item.actual_weight).toFixed(2)) : '',
          item.notes || '',
        ]);
      });
    }

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(allRows);

    // Auto-fit column widths per section so wider sections don't stretch narrower ones
    // Find section boundaries (each section starts with a title row like ['PRODUCTS'])
    const sectionTitleValues = [
      'PRODUCTS', 'STATIONS', 'OPERATORS', 'ALL SESSIONS', 'WASTE LOG',
      'TRANSFER OUT — RAW MATERIALS KPI', 'TRANSFER OUT — FINISHED PRODUCTS', 'TRANSFER OUT — REJECTED MATERIALS', 'TRANSFER OUT — REMAINING RAW MATERIALS', 'TRANSFER OUT — BY-PRODUCTS',
    ];
    const sections: { start: number; end: number; cols: number }[] = [];
    let currentStart = -1;
    allRows.forEach((row, i) => {
      if (row.length === 1 && sectionTitleValues.includes(String(row[0]))) {
        if (currentStart >= 0) {
          sections.push({ start: currentStart, end: i - 1, cols: Math.max(...allRows.slice(currentStart, i).map(r => r.length)) });
        }
        currentStart = i;
      }
    });
    if (currentStart >= 0) {
      sections.push({ start: currentStart, end: allRows.length - 1, cols: Math.max(...allRows.slice(currentStart).map(r => r.length)) });
    }

    const maxCols = Math.max(...allRows.map(r => r.length));
    ws['!cols'] = Array.from({ length: maxCols }, (_, colIndex) => {
      let maxLength = 8;
      // Only consider sections that actually have this column
      sections.forEach(sec => {
        if (colIndex < sec.cols) {
          for (let r = sec.start; r <= sec.end; r++) {
            const cellLength = String(allRows[r][colIndex] ?? '').length;
            if (cellLength > maxLength) maxLength = cellLength;
          }
        }
      });
      return { wch: Math.min(maxLength + 2, 55) };
    });

    // Bold section titles and header rows
    const boldStyle = { font: { bold: true } };
    const headerStyle = {
      fill: { fgColor: { rgb: 'E5E7EB' } },
      font: { bold: true },
      border: { bottom: { style: 'thin', color: { rgb: '9CA3AF' } } }
    };
    const sectionTitles = [
      'PRODUCTS', 'STATIONS', 'OPERATORS', 'ALL SESSIONS', 'WASTE LOG',
      'TRANSFER OUT — RAW MATERIALS KPI', 'TRANSFER OUT — FINISHED PRODUCTS', 'TRANSFER OUT — REJECTED MATERIALS', 'TRANSFER OUT — REMAINING RAW MATERIALS', 'TRANSFER OUT — BY-PRODUCTS',
    ];
    allRows.forEach((row, rowIndex) => {
      if (row.length === 1 && sectionTitles.includes(String(row[0]))) {
        const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: 0 });
        if (ws[cellRef]) ws[cellRef].s = boldStyle;
      }
      // Header row is the row right after a section title
      if (rowIndex > 0) {
        const prevRow = allRows[rowIndex - 1];
        if (prevRow.length === 1 && sectionTitles.includes(String(prevRow[0]))) {
          row.forEach((_, colIndex) => {
            const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
            if (ws[cellRef]) ws[cellRef].s = headerStyle;
          });
        }
      }
    });

    // Apply wrapText to product rows with multiline raw material cells
    if (excelHasRawMaterials) {
      for (let r = productStartRow + 2; r <= productEndRow; r++) {
        // Columns 1 (Raw Material), 2 (Allocated), 3 (Used) may have newlines
        for (let c = 1; c <= 3; c++) {
          const cellRef = XLSX.utils.encode_cell({ r, c });
          if (ws[cellRef]) {
            ws[cellRef].s = { ...(ws[cellRef].s || {}), alignment: { wrapText: true, vertical: 'top' } };
          }
        }
        // Also align product name and numeric columns to top
        for (const c of [0, 4, 5, 6, 7]) {
          const cellRef = XLSX.utils.encode_cell({ r, c });
          if (ws[cellRef]) {
            ws[cellRef].s = { ...(ws[cellRef].s || {}), alignment: { vertical: 'top' } };
          }
        }
      }
    }

    // Apply background colors to waste log columns (header + data rows)
    // Columns 2-5: Actual Waste (red tint), Column 5: subtotal (darker red)
    // Columns 6-10: Returns to Inventory (green tint), Column 10: subtotal (darker green)
    const wasteRedBg = { fill: { fgColor: { rgb: 'FEE2E2' } } };         // light red
    const wasteRedBold = { fill: { fgColor: { rgb: 'FECACA' } }, font: { bold: true } };  // darker red for subtotal
    const returnGreenBg = { fill: { fgColor: { rgb: 'DCFCE7' } } };      // light green
    const returnGreenBold = { fill: { fgColor: { rgb: 'BBF7D0' } }, font: { bold: true } }; // darker green for subtotal
    for (let r = wasteLogStartRow + 1; r < allRows.length; r++) {
      const row = allRows[r];
      if (row.length < 12) continue;
      // Actual waste columns (2,3,4)
      for (let c = 2; c <= 4; c++) {
        const cellRef = XLSX.utils.encode_cell({ r, c });
        if (ws[cellRef]) ws[cellRef].s = r === wasteLogStartRow + 1
          ? { ...headerStyle, fill: { fgColor: { rgb: 'FECACA' } } }
          : wasteRedBg;
      }
      // Waste subtotal column (5)
      const wasteSubRef = XLSX.utils.encode_cell({ r, c: 5 });
      if (ws[wasteSubRef]) ws[wasteSubRef].s = r === wasteLogStartRow + 1
        ? { ...headerStyle, fill: { fgColor: { rgb: 'FECACA' } } }
        : wasteRedBold;
      // Returns columns (6,7,8,9)
      for (let c = 6; c <= 9; c++) {
        const cellRef = XLSX.utils.encode_cell({ r, c });
        if (ws[cellRef]) ws[cellRef].s = r === wasteLogStartRow + 1
          ? { ...headerStyle, fill: { fgColor: { rgb: 'BBF7D0' } } }
          : returnGreenBg;
      }
      // Returns subtotal column (10)
      const retSubRef = XLSX.utils.encode_cell({ r, c: 10 });
      if (ws[retSubRef]) ws[retSubRef].s = r === wasteLogStartRow + 1
        ? { ...headerStyle, fill: { fgColor: { rgb: 'BBF7D0' } } }
        : returnGreenBold;
      // Total column (11) — distinct blue/dark style
      const totalRef = XLSX.utils.encode_cell({ r, c: 11 });
      if (ws[totalRef]) ws[totalRef].s = r === wasteLogStartRow + 1
        ? { ...headerStyle, fill: { fgColor: { rgb: 'DBEAFE' } } }
        : { fill: { fgColor: { rgb: 'DBEAFE' } }, font: { bold: true } };
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'EOD Summary');
    XLSX.writeFile(wb, `eod-summary-${selectedDate}.xlsx`);
  };

  // Tab configuration
  const tabs = [
    { id: 'product' as TabType, label: t('endOfDaySummary.byProduct') },
    { id: 'station' as TabType, label: t('endOfDaySummary.byStation') },
    { id: 'worker' as TabType, label: t('endOfDaySummary.byWorker') },
    { id: 'sessions' as TabType, label: t('endOfDaySummary.allSessions') },
    { id: 'waste' as TabType, label: t('endOfDaySummary.wasteLog') },
  ];

  return (
    <div className={`space-y-6 print:p-2 ${embedded ? '' : 'p-4 md:p-6'}`}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 print:hidden">
        {!embedded && (
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('endOfDaySummary.title')}
          </h1>
        )}

        <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${embedded ? 'w-full' : ''}`}>
        <div className="flex items-center justify-between gap-2 px-4 py-3">
          {/* Left: Today button */}
          <div className="flex items-center gap-2 justify-self-start">
            {!isToday && (
              <button
                onClick={() => setSelectedDate(getLocalDateString())}
                className="px-3 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors border border-indigo-300 dark:border-indigo-600"
              >
                {t('endOfDaySummary.today')}
              </button>
            )}
          </div>

          {/* Center: Date navigation */}
          <div className="flex items-center justify-center gap-2 md:gap-4">
            <button
              onClick={() => navigateDate('prev')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={t('endOfDaySummary.previousDay')}
            >
              <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <DatePickerDropdown
              selected={selectedDate}
              onSelect={(date) => setSelectedDate(date)}
              max={getLocalDateString()}
            >
              {({ onClick }) => (
                <button
                  type="button"
                  onClick={onClick}
                  className="bg-transparent border-none cursor-pointer"
                  title={t('endOfDaySummary.selectDate') || 'Select date'}
                >
                  <span className="text-base md:text-lg font-semibold text-gray-900 dark:text-white min-w-[130px] md:min-w-[220px] text-center inline-block whitespace-nowrap">
                    {formatDateForDisplay(selectedDate)}
                  </span>
                </button>
              )}
            </DatePickerDropdown>
            <button
              onClick={() => navigateDate('next')}
              disabled={isToday}
              className={`p-2 rounded-lg transition-colors ${isToday ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'}`}
              title={t('endOfDaySummary.nextDay')}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Right: Refresh + Print/Export (sm+) or single kebab menu (mobile) */}
          <div className="flex items-center gap-2 justify-self-end">
            {/* Desktop / tablet: 3 separate buttons */}
            <button
              onClick={() => refetch()}
              className="hidden sm:flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              title={t('common.refresh')}
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handlePrint}
              className="hidden sm:inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-gray-600 rounded-lg hover:bg-gray-700"
            >
              <Printer className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('common.print')}</span>
            </button>
            <button
              onClick={handleExportExcel}
              className="hidden sm:inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <Download className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('common.export')}</span>
            </button>

            {/* Mobile: single kebab button with dropdown */}
            <div className="relative sm:hidden" ref={actionsMenuRef}>
              <button
                type="button"
                onClick={() => setActionsMenuOpen((v) => !v)}
                className="p-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                title={t('common.moreActions') || 'More actions'}
                aria-haspopup="menu"
                aria-expanded={actionsMenuOpen}
              >
                <MoreVertical className="w-5 h-5" />
              </button>
              {actionsMenuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 py-1"
                >
                  <button
                    role="menuitem"
                    onClick={() => { setActionsMenuOpen(false); refetch(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                    {t('common.refresh')}
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => { setActionsMenuOpen(false); handlePrint(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Printer className="w-4 h-4" />
                    {t('common.print')}
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => { setActionsMenuOpen(false); handleExportExcel(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Download className="w-4 h-4" />
                    {t('common.export')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Print Header (only visible when printing) */}
      <div className="hidden print:block mb-4">
        <h1 className="text-xl font-bold">{t('endOfDaySummary.title')}</h1>
        <p className="text-sm text-gray-600">{selectedDate}</p>
      </div>

      {/* Main Content */}
      <div className="space-y-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : isError ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-center text-red-700 dark:text-red-400">
            {t('endOfDaySummary.errorLoading')}
          </div>
        ) : data ? (
          <>
            {/* Summary Cards — collapse when tab is re-clicked */}
            {!summaryCollapsed && <SummaryCards summary={data.summary} />}

            {/* Alerts Section — also collapses with summary */}
            {!summaryCollapsed && data.alerts.length > 0 && (
              <AlertsSection
                alerts={data.alerts}
                onDismissAlert={handleDismissAlert}
                canDismissFreshnessAlerts={isToday}
              />
            )}

            {/* Tabs */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              {/* Tab Headers */}
              <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto print:hidden">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="p-4">
                {activeTab === 'product' && (
                  <ProductTable products={data.by_product} />
                )}
                {activeTab === 'station' && (
                  <StationTable stations={data.by_station} />
                )}
                {activeTab === 'worker' && (
                  <WorkerTable workers={data.by_worker} />
                )}
                {activeTab === 'sessions' && (
                  <SessionsTable sessions={data.sessions} />
                )}
                {activeTab === 'waste' && (
                  <WasteTable wasteBySession={data.waste_by_session} />
                )}
              </div>
            </div>

            {/* Transfer Out Summary Section */}
            <TransferOutSection data={transferOutData} isLoading={transferOutLoading} />
          </>
        ) : (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {t('endOfDaySummary.noData')}
          </div>
        )}
      </div>

    </div>
  );
};

export default EndOfDaySummary;
